import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Users, FileText } from 'lucide-react';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell,
} from 'recharts';
import AppLayout from '../layout/AppLayout';
import MobileHeader from '../layout/MobileHeader';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';

// ─── Helpers ─────────────────────────────────────────────────────────────────
const CURRENCY_SYMBOLS: Record<string, string> = {
  EUR: '€', USD: '$', GBP: '£', MXN: '$', BRL: 'R$',
  ARS: '$', CLP: '$', COP: '$', PEN: 'S/', CAD: '$', AUD: '$',
};
const getCurrencySymbol = (code?: string | null) =>
  code ? (CURRENCY_SYMBOLS[code] ?? code) : '€';

const fmtE  = (n: number, sym = '€') => sym + Math.round(n).toLocaleString('en-US');
const fmtE2 = (n: number, sym = '€') =>
  sym + parseFloat(n.toFixed(2)).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtN  = (n: number, d = 1) => parseFloat(n.toFixed(d)).toLocaleString('en-US');

// ─── Sub-components ───────────────────────────────────────────────────────────

const TabGroup: React.FC<{ options: { id: string; label: string }[]; value: string; onChange: (id: string) => void }> = ({ options, value, onChange }) => (
  <div className="flex gap-0.5 bg-gray-100 rounded-lg p-0.5 border border-gray-200">
    {options.map((o) => (
      <button key={o.id} onClick={() => onChange(o.id)}
        className={`text-[11px] font-medium px-2.5 py-1 rounded-md whitespace-nowrap transition-all duration-150 ${
          value === o.id ? 'bg-white text-[#1E0DB8] font-bold shadow-sm' : 'text-gray-500 hover:text-gray-800'
        }`}>
        {o.label}
      </button>
    ))}
  </div>
);

const FieldRow: React.FC<{ label: string; children: React.ReactNode; note?: string }> = ({ label, children, note }) => (
  <div className="flex flex-col mb-1.5">
    <div className="flex items-center justify-between gap-2">
      <label className="text-[11px] font-medium text-gray-600 flex-1 leading-tight">{label}</label>
      {children}
    </div>
    {note && <p className="text-[9px] italic text-gray-400 mt-0.5">{note}</p>}
  </div>
);

const NumInput: React.FC<{ value: number; onChange: (v: number) => void; min?: number; max?: number; step?: number; unit?: string; w?: string }> =
  ({ value, onChange, min, max, step = 1, unit, w = 'w-16' }) => (
    <div className="flex items-center gap-1 flex-shrink-0">
      <input type="number" value={value} min={min} max={max} step={step}
        onChange={(e) => onChange(parseFloat(e.target.value) || 0)}
        className={`${w} text-[11px] font-medium text-right px-1.5 py-1 rounded-md border border-gray-200 bg-gray-50 text-gray-900 outline-none focus:border-[#1E0DB8] focus:bg-white transition-colors [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none`}
      />
      {unit && <span className="text-[10px] text-gray-400 w-4">{unit}</span>}
    </div>
  );

const DerivedRow: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <div className="flex items-center justify-between bg-gray-50 border border-gray-100 rounded-md px-2 py-1 mb-1.5">
    <span className="text-[10px] text-gray-400 italic flex-1">{label}</span>
    <span className="text-[11px] font-semibold text-[#1E0DB8]">{value}</span>
  </div>
);

const Card: React.FC<{ title?: string; children: React.ReactNode; className?: string }> = ({ title, children, className = '' }) => (
  <div className={`bg-white border border-gray-200 rounded-xl p-3 ${className}`}>
    {title && <p className="text-[9px] font-bold text-gray-400 uppercase tracking-widest mb-2">{title}</p>}
    {children}
  </div>
);

const Divider = () => <div className="border-t border-gray-100 my-2" />;

const CustomTooltip = ({ active, payload, label, sym = '€' }: any) => {
  if (active && payload?.length) {
    return (
      <div className="bg-white border border-gray-200 rounded-lg px-2.5 py-1.5 shadow-lg">
        <p className="text-[10px] font-bold text-gray-500">{label}</p>
        <p className="text-xs font-semibold text-[#1E0DB8]">{sym + (payload[0].value as number).toLocaleString('en-US')}</p>
      </div>
    );
  }
  return null;
};

// ─── Main Component ───────────────────────────────────────────────────────────
const ROICalculatorPage: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { currentUser } = useAuth();

  // Currency symbol derived from org-level setting (falls back to €)
  const currSym = getCurrencySymbol(currentUser?.roi_currency);
  // Bound formatting helpers using the org currency symbol
  const fmtE  = (n: number) => getCurrencySymbol(currentUser?.roi_currency) + Math.round(n).toLocaleString('en-US');
  const fmtE2 = (n: number) => getCurrencySymbol(currentUser?.roi_currency) + parseFloat(n.toFixed(2)).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  const [volumeMode, setVolumeMode]             = useState<'orders' | 'quotes'>('quotes');
  const [ordersPerMonth, setOrdersPerMonth]      = useState(6000);
  const [convOrders, setConvOrders]             = useState(40);
  const [quotesDirectInput, setQuotesDirectInput] = useState(currentUser?.roi_monthly_quotes ?? 15000);
  const [convQuotes, setConvQuotes]             = useState(40);
  const [avgTotal, setAvgTotal]                 = useState(303.21);
  const [avgParts, setAvgParts]                 = useState(248.93);
  const [fteCap, setFteCap]                     = useState(352);
  const [fteCost, setFteCost]                   = useState(currentUser?.roi_fte_cost ?? 3281.68);
  const [costMode, setCostMode]                 = useState<'per-quote' | 'fixed'>('per-quote');
  const [cataCostPerQ, setCataCostPerQ]         = useState(currentUser?.roi_cost_per_quote ?? 0.7);
  const [cataCostFixed, setCataCostFixed]       = useState((currentUser?.roi_monthly_quotes ?? 15000) * 0.70);
  const [fteMultiplier, setFteMultiplier]       = useState(currentUser?.roi_fte_multiplier ?? 4);
  const [basketUplift, setBasketUplift]         = useState(2);
  const [convUplift, setConvUplift]             = useState(2);
  const [errorRate, setErrorRate]               = useState(5.5);
  const [revLogistics, setRevLogistics]         = useState(17.5);
  const [itWorkflow, setItWorkflow]             = useState<number | ''>('');
  const [itInteg, setItInteg]                   = useState<number | ''>('');

  const getMQ = useCallback((forceMode?: 'orders' | 'quotes') => {
    const mode = forceMode ?? volumeMode;
    if (mode === 'orders') {
      const conv = convOrders / 100;
      return conv > 0 ? ordersPerMonth / conv : 0;
    }
    return quotesDirectInput;
  }, [volumeMode, ordersPerMonth, convOrders, quotesDirectInput]);

  const mq         = getMQ();
  const conv        = (volumeMode === 'orders' ? convOrders : convQuotes) / 100;
  const cataCost    = costMode === 'per-quote' ? mq * cataCostPerQ : cataCostFixed;
  const multiplier  = Math.max(1.01, fteMultiplier);
  const timeSaved   = 1 - (1 / multiplier);
  const ftes        = fteCap > 0 ? mq / fteCap : 0;
  const fteSaved    = ftes * timeSaved;
  const labor       = ftes * fteCost * timeSaved;
  const bsk         = mq * avgTotal * (basketUplift / 100);
  const cvp         = mq * avgTotal * (convUplift / 100);
  const err         = mq * avgParts * (errorRate / 100) * (revLogistics / 100);
  const opTotal     = labor + bsk + cvp + err;
  const itTotal     = (Number(itWorkflow) || 0) + (Number(itInteg) || 0);
  const total       = opTotal + itTotal;
  const net         = total - cataCost;
  const roi         = cataCost > 0 ? ((total - cataCost) / cataCost) * 100 : 0;
  const cpqDisplay  = mq > 0 ? cataCost / mq : 0;
  const monthlyOrders = conv > 0 ? mq * conv : 0;

  const handleVolumeModeChange = (mode: string) => {
    const m = mode as 'orders' | 'quotes';
    if (m === 'quotes') { setConvQuotes(convOrders); setQuotesDirectInput(Math.round(getMQ('orders'))); }
    else { setConvOrders(convQuotes); }
    setVolumeMode(m);
  };

  const handleCostModeChange = (mode: string) => {
    const m = mode as 'per-quote' | 'fixed';
    if (m === 'fixed') setCataCostFixed(Math.round(mq * cataCostPerQ));
    else setCataCostPerQ(mq > 0 ? parseFloat((cataCostFixed / mq).toFixed(2)) : 0);
    setCostMode(m);
  };

  const chartData = [
    { name: t('roiCalculator.results.chartWorkforce'), value: Math.round(labor), color: '#1E0DB8' },
    { name: t('roiCalculator.results.chart247'),       value: Math.round(bsk),  color: '#137F67' },
    { name: t('roiCalculator.results.chartUpsell'),    value: Math.round(cvp),   color: '#3627BF' },
    { name: t('roiCalculator.results.chartErrors'),    value: Math.round(err),   color: '#8B89FF' },
  ];

  const generateExecutiveSummary = useCallback(() => {
    const roiFmt   = Math.round(roi).toLocaleString('en-US') + '%';
    const multFmt  = '×' + parseFloat(multiplier.toFixed(1));
    const mqFmt    = Math.round(mq).toLocaleString('en-US');
    // Currency-aware formatters captured in this closure
    const sym = currSym;
    const fe  = (n: number) => sym + Math.round(n).toLocaleString('en-US');
    const fe2 = (n: number) => sym + parseFloat(n.toFixed(2)).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    const currencyLabel = currentUser?.roi_currency ?? 'EUR';

    const es = (key: string, opts?: Record<string, string | number>) =>
      t(`roiCalculator.executiveSummary.${key}`, opts as any);

    const html = `<!DOCTYPE html>
<html lang="en">
<head>
<meta charset="UTF-8">
<meta name="viewport" content="width=device-width, initial-scale=1.0">
<title>${es('pageTitle')}</title>
<link rel="preconnect" href="https://fonts.googleapis.com">
<link href="https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;700&family=Inter+Tight:wght@400;500;700&display=swap" rel="stylesheet">
<style>
:root {
  --navy:#1E0DB8; --indigo:#3627BF; --lilac:#DBD8FF;
  --tag-bg:#E5E2FF; --tag-fg:#3627BF;
  --bg-page:#FAF9F6; --bg-surface:#FFFFFF; --bg-subtle:#F6F4F0;
  --text-primary:#201E1A; --text-muted:#999999;
  --success:#137F67; --success-bg:#C7FFE8;
  --border:rgba(32,30,26,0.12);
  --ui:'Plus Jakarta Sans',sans-serif;
  --hd:'Inter Tight',sans-serif;
}
*,*::before,*::after{box-sizing:border-box;margin:0;padding:0;}
html{scroll-behavior:smooth;}
body{background:var(--bg-page);font-family:var(--ui);color:var(--text-primary);}
.hero{background:var(--navy);padding:64px 48px 72px;position:relative;overflow:hidden;}
.hero::before{content:'';position:absolute;top:-120px;right:-120px;width:480px;height:480px;border-radius:50%;background:var(--indigo);opacity:0.25;}
.hero::after{content:'';position:absolute;bottom:-80px;left:30%;width:320px;height:320px;border-radius:50%;background:var(--lilac);opacity:0.08;}
.hero-inner{max-width:900px;margin:0 auto;position:relative;z-index:1;}
.hero-badge{display:inline-flex;align-items:center;gap:8px;background:rgba(255,255,255,0.12);border:0.5px solid rgba(255,255,255,0.2);border-radius:100px;padding:6px 16px;font-size:11px;font-weight:700;color:var(--lilac);letter-spacing:0.8px;text-transform:uppercase;margin-bottom:28px;}
.hero-title{font-family:var(--hd);font-size:clamp(28px,4vw,48px);font-weight:700;color:#fff;line-height:1.15;margin-bottom:20px;max-width:700px;}
.hero-title em{color:var(--lilac);font-style:normal;}
.hero-subtitle{font-size:16px;font-weight:400;color:rgba(219,216,255,0.75);max-width:560px;line-height:1.7;margin-bottom:48px;}
.hero-stats{display:grid;grid-template-columns:repeat(3,1fr);gap:1px;background:rgba(255,255,255,0.1);border-radius:12px;overflow:hidden;max-width:680px;}
.hero-stat{background:rgba(255,255,255,0.06);padding:20px 24px;}
.hero-stat-num{font-family:var(--hd);font-size:28px;font-weight:700;color:#fff;line-height:1;margin-bottom:6px;}
.hero-stat-lbl{font-size:11px;color:rgba(219,216,255,0.65);font-weight:500;line-height:1.4;}
.page{max-width:960px;margin:0 auto;padding:0 32px;}
.section-wrap{padding:64px 0 0;}
.section-kicker{display:inline-flex;align-items:center;gap:8px;font-size:10px;font-weight:700;letter-spacing:1px;text-transform:uppercase;color:var(--navy);margin-bottom:10px;}
.section-kicker::before{content:'';width:20px;height:2px;background:var(--navy);border-radius:2px;display:block;}
.section-title{font-family:var(--hd);font-size:clamp(20px,2.5vw,28px);font-weight:700;color:var(--text-primary);margin-bottom:8px;line-height:1.25;}
.section-lead{font-size:15px;color:var(--text-muted);max-width:560px;line-height:1.7;margin-bottom:40px;}
.cards-grid{display:grid;grid-template-columns:repeat(2,1fr);gap:16px;margin-bottom:16px;}
.vcard{background:var(--bg-surface);border:0.5px solid var(--border);border-radius:16px;padding:28px 28px 24px;display:flex;flex-direction:column;gap:16px;}
.vcard-icon{width:40px;height:40px;border-radius:10px;display:flex;align-items:center;justify-content:center;flex-shrink:0;}
.vcard-header{display:flex;align-items:flex-start;gap:14px;}
.vcard-header-text{flex:1;}
.vcard-title{font-family:var(--hd);font-size:15px;font-weight:700;color:var(--text-primary);line-height:1.3;margin-bottom:4px;}
.vcard-subtitle{font-size:11px;font-weight:600;color:var(--navy);text-transform:uppercase;letter-spacing:0.5px;}
.vcard-body{font-size:13px;color:var(--text-muted);line-height:1.75;flex:1;}
.vcard-body strong{color:var(--text-primary);font-weight:600;}
.vcard-value{display:flex;align-items:baseline;justify-content:space-between;padding-top:16px;border-top:0.5px solid var(--border);gap:12px;}
.vcard-value-num{font-family:var(--hd);font-size:24px;font-weight:700;color:var(--navy);line-height:1;}
.vcard-value-lbl{font-size:11px;color:var(--text-muted);font-weight:500;text-align:right;max-width:160px;line-height:1.4;}
.vcard.it{border-color:rgba(30,13,184,0.12);background:var(--tag-bg);}
.vcard.it .vcard-title{color:var(--navy);}
.vcard.it .vcard-body{color:var(--tag-fg);}
.vcard.it .vcard-body strong{color:var(--indigo);}
.vcard.it .vcard-value{border-top-color:rgba(30,13,184,0.15);}
.vcard.it .vcard-value-num{color:var(--indigo);}
.summary{margin:48px 0 64px;background:var(--navy);border-radius:20px;padding:40px 48px;display:flex;align-items:center;gap:48px;position:relative;overflow:hidden;}
.summary::after{content:'';position:absolute;right:-60px;top:-60px;width:280px;height:280px;border-radius:50%;background:var(--indigo);opacity:0.3;}
.summary-left{flex:1;position:relative;z-index:1;}
.summary-title{font-family:var(--hd);font-size:22px;font-weight:700;color:#fff;margin-bottom:12px;line-height:1.3;}
.summary-body{font-size:14px;color:rgba(219,216,255,0.75);line-height:1.75;max-width:480px;}
.summary-body strong{color:#fff;}
.summary-right{flex-shrink:0;text-align:center;position:relative;z-index:1;}
.summary-roi-num{font-family:var(--hd);font-size:64px;font-weight:700;color:#fff;line-height:1;}
.summary-roi-lbl{font-size:12px;color:var(--lilac);font-weight:600;letter-spacing:0.5px;margin-top:4px;}
.summary-metrics{display:flex;gap:32px;margin-top:24px;}
.summary-metric-num{font-family:var(--hd);font-size:20px;font-weight:700;color:#fff;}
.summary-metric-lbl{font-size:11px;color:rgba(219,216,255,0.6);margin-top:2px;}
.params-strip{margin:0 0 80px;border:0.5px solid var(--border);border-radius:16px;padding:24px 28px;}
.params-title{font-family:var(--hd);font-size:13px;font-weight:700;color:var(--navy);margin-bottom:14px;text-transform:uppercase;letter-spacing:0.5px;}
.params-grid{display:grid;grid-template-columns:repeat(3,1fr);gap:10px 24px;}
.param-item{display:flex;flex-direction:column;gap:2px;}
.param-lbl{font-size:10px;color:var(--text-muted);font-weight:500;}
.param-val{font-size:13px;font-weight:700;color:var(--text-primary);}
@media print{.params-strip{margin:0 0 12px!important;padding:10px 16px!important;border-radius:8px;}.params-grid{gap:6px 16px;}.param-lbl{font-size:8px;}.param-val{font-size:10px;}}
.pilot-badge{background:var(--navy);color:#fff;font-size:10px;font-weight:700;border-radius:6px;padding:5px 10px;letter-spacing:0.5px;text-transform:uppercase;white-space:nowrap;}
.pilot-text{flex:1;}
.pilot-title{font-size:15px;font-weight:700;color:var(--navy);margin-bottom:4px;}
.pilot-body{font-size:13px;color:var(--tag-fg);line-height:1.6;}
.print-btn{position:fixed;bottom:28px;right:28px;background:var(--navy);color:#fff;border:none;border-radius:12px;padding:12px 24px;font-family:var(--ui);font-size:13px;font-weight:700;cursor:pointer;box-shadow:0 4px 16px rgba(30,13,184,0.35);z-index:100;display:flex;align-items:center;gap:8px;}
.print-btn:hover{background:var(--indigo);}
@media print{
  @page{size:A4 landscape;margin:10mm 12mm;}
  *{-webkit-print-color-adjust:exact!important;print-color-adjust:exact!important;}
  .print-btn{display:none!important;}
  body{background:#fff;font-size:11px;}
  .hero{padding:16px 24px 18px!important;}
  .hero::before,.hero::after{display:none;}
  .hero-badge{margin-bottom:8px;padding:3px 10px;font-size:9px;}
  .hero-title{font-size:20px!important;margin-bottom:6px;}
  .hero-subtitle{font-size:11px;margin-bottom:12px;line-height:1.4;}
  .hero-stats{max-width:100%;gap:0;}
  .hero-stat{padding:8px 14px;}
  .hero-stat-num{font-size:18px;}
  .hero-stat-lbl{font-size:9px;}
  .page{padding:0 12px;max-width:100%;}
  .section-wrap{padding:12px 0 0!important;}
  .section-kicker{font-size:8px;margin-bottom:4px;}
  .section-title{font-size:14px!important;margin-bottom:3px;}
  .section-lead{font-size:10px;margin-bottom:10px;line-height:1.4;}
  .cards-grid{grid-template-columns:repeat(2,1fr);gap:8px;margin-bottom:8px;}
  .vcard{padding:10px 12px 8px!important;gap:6px!important;border-radius:8px;}
  .vcard-icon{width:24px;height:24px;border-radius:6px;}
  .vcard-icon svg{width:13px;height:13px;}
  .vcard-header{gap:8px;}
  .vcard-subtitle{font-size:8px;}
  .vcard-title{font-size:11px;margin-bottom:2px;}
  .vcard-body{font-size:9px;line-height:1.45;}
  .vcard-value{padding-top:6px;}
  .vcard-value-num{font-size:15px;}
  .vcard-value-lbl{font-size:8px;}
  .summary{margin:10px 0 10px!important;padding:16px 24px!important;border-radius:10px;gap:24px;}
  .summary::after{display:none;}
  .summary-title{font-size:14px;margin-bottom:6px;}
  .summary-body{font-size:10px;line-height:1.45;}
  .summary-roi-num{font-size:36px!important;}
  .summary-roi-lbl{font-size:9px;}
  .summary-metrics{gap:16px;margin-top:10px;}
  .summary-metric-num{font-size:14px;}
  .summary-metric-lbl{font-size:8px;}
  .pilot-strip{margin:0 0 12px!important;padding:10px 16px!important;border-radius:8px;gap:12px;}
  .pilot-badge{font-size:8px;padding:3px 7px;}
  .pilot-title{font-size:11px;margin-bottom:2px;}
  .pilot-body{font-size:9px;line-height:1.4;}
}
@media(max-width:700px){.hero{padding:40px 24px 56px;}.page{padding:0 20px;}.cards-grid{grid-template-columns:1fr;}.hero-stats{grid-template-columns:1fr;}.summary{flex-direction:column;gap:24px;padding:28px;}.summary-roi-num{font-size:48px;}.pilot-strip{flex-direction:column;align-items:flex-start;}}
</style>
</head>
<body>
<button class="print-btn" onclick="window.print()">
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="6 9 6 2 18 2 18 9"/><path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"/><rect x="6" y="14" width="12" height="8"/></svg>
  ${es('printBtn')}
</button>

<div class="hero">
  <div class="hero-inner">
    <div class="hero-badge">
      <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="22 7 13.5 15.5 8.5 10.5 2 17"/></svg>
      ${es('heroBadge')}
    </div>
    <h1 class="hero-title">${es('heroTitle')}<br><em>${es('heroTitleEm')}</em></h1>
    <p class="hero-subtitle">${es('heroSubtitle')}</p>
    <div class="hero-stats">
      <div class="hero-stat">
        <div class="hero-stat-num">${fe(total)}</div>
        <div class="hero-stat-lbl">${es('statEstimatedValue')}</div>
      </div>
      <div class="hero-stat">
        <div class="hero-stat-num">${roiFmt}</div>
        <div class="hero-stat-lbl">${es('statMonthlyRoi')}</div>
      </div>
      <div class="hero-stat">
        <div class="hero-stat-num">${mqFmt}</div>
        <div class="hero-stat-lbl">${es('statQuotesHandled')}</div>
      </div>
    </div>
  </div>
</div>

<div class="page">

  <div class="section-wrap">
    <div class="section-kicker">${es('opKicker')}</div>
    <h2 class="section-title">${es('opTitle')}</h2>
    <p class="section-lead">${es('opLead')}</p>
    <div class="cards-grid">

      <div class="vcard">
        <div class="vcard-header">
          <div class="vcard-icon" style="background:#E6F1FB;">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#185FA5" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>
          </div>
          <div class="vcard-header-text">
            <div class="vcard-subtitle">${es('workforceSubtitle')}</div>
            <div class="vcard-title">${es('workforceTitle')}</div>
          </div>
        </div>
        <p class="vcard-body">${es('workforceBody', { mult: multFmt, fte: fmtN(fteSaved) })}</p>
        <div class="vcard-value">
          <div class="vcard-value-num">${fe(labor)}</div>
          <div class="vcard-value-lbl">${es('workforceValueLbl')}</div>
        </div>
      </div>

      <div class="vcard">
        <div class="vcard-header">
          <div class="vcard-icon" style="background:#D4EFDF;">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#137F67" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 12 19.79 19.79 0 0 1 1.64 3.18 2 2 0 0 1 3.6 1h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L7.91 8.6a16 16 0 0 0 6.06 6.06l.91-.91a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 21.73 17z"/></svg>
          </div>
          <div class="vcard-header-text">
            <div class="vcard-subtitle">${es('quotingSubtitle')}</div>
            <div class="vcard-title">${es('quotingTitle')}</div>
          </div>
        </div>
        <p class="vcard-body">${es('quotingBody', { mq: mqFmt })}</p>
        <div class="vcard-value">
          <div class="vcard-value-num">${fe(bsk)}</div>
          <div class="vcard-value-lbl">${es('quotingValueLbl')}</div>
        </div>
      </div>

      <div class="vcard">
        <div class="vcard-header">
          <div class="vcard-icon" style="background:#E5E2FF;">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#3627BF" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><polyline points="22 7 13.5 15.5 8.5 10.5 2 17"/><polyline points="16 7 22 7 22 13"/></svg>
          </div>
          <div class="vcard-header-text">
            <div class="vcard-subtitle">${es('upsellSubtitle')}</div>
            <div class="vcard-title">${es('upsellTitle')}</div>
          </div>
        </div>
        <p class="vcard-body">${es('upsellBody', { pct: parseFloat((convUplift).toFixed(1)) })}</p>
        <div class="vcard-value">
          <div class="vcard-value-num">${fe(cvp)}</div>
          <div class="vcard-value-lbl">${es('upsellValueLbl')}</div>
        </div>
      </div>

      <div class="vcard">
        <div class="vcard-header">
          <div class="vcard-icon" style="background:#FEF3C7;">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#B45309" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><polyline points="9 11 12 14 22 4"/><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"/></svg>
          </div>
          <div class="vcard-header-text">
            <div class="vcard-subtitle">${es('errorSubtitle')}</div>
            <div class="vcard-title">${es('errorTitle')}</div>
          </div>
        </div>
        <p class="vcard-body">${es('errorBody', { pct: parseFloat((errorRate).toFixed(1)) })}</p>
        <div class="vcard-value">
          <div class="vcard-value-num">${fe(err)}</div>
          <div class="vcard-value-lbl">${es('errorValueLbl')}</div>
        </div>
      </div>

    </div>
  </div>

  ${(itTotal > 0) ? `
  <div class="section-wrap">
    <div class="section-kicker">${es('itKicker')}</div>
    <h2 class="section-title">${es('itTitle')}</h2>
    <p class="section-lead">${es('itLead')}</p>
    <div class="cards-grid">
      ${Number(itWorkflow) > 0 ? `
      <div class="vcard it">
        <div class="vcard-header">
          <div class="vcard-icon" style="background:rgba(30,13,184,0.12);">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#1E0DB8" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><rect x="2" y="3" width="20" height="14" rx="2"/><line x1="8" y1="21" x2="16" y2="21"/><line x1="12" y1="17" x2="12" y2="21"/></svg>
          </div>
          <div class="vcard-header-text">
            <div class="vcard-subtitle" style="color:var(--indigo);">${es('itWorkflowSubtitle')}</div>
            <div class="vcard-title">${es('itWorkflowTitle')}</div>
          </div>
        </div>
        <p class="vcard-body">${es('itWorkflowBody', { mq: mqFmt })}</p>
        <div class="vcard-value">
          <div class="vcard-value-num" style="color:var(--indigo);">${fe(Number(itWorkflow))}</div>
          <div class="vcard-value-lbl" style="color:var(--tag-fg);">${es('itValueLbl')}</div>
        </div>
      </div>` : ''}
      ${Number(itInteg) > 0 ? `
      <div class="vcard it">
        <div class="vcard-header">
          <div class="vcard-icon" style="background:rgba(30,13,184,0.12);">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#1E0DB8" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M12 2L2 7l10 5 10-5-10-5z"/><path d="M2 17l10 5 10-5"/><path d="M2 12l10 5 10-5"/></svg>
          </div>
          <div class="vcard-header-text">
            <div class="vcard-subtitle" style="color:var(--indigo);">${es('itIntegSubtitle')}</div>
            <div class="vcard-title">${es('itIntegTitle')}</div>
          </div>
        </div>
        <p class="vcard-body">${es('itIntegBody')}</p>
        <div class="vcard-value">
          <div class="vcard-value-num" style="color:var(--indigo);">${fe(Number(itInteg))}</div>
          <div class="vcard-value-lbl" style="color:var(--tag-fg);">${es('itValueLbl')}</div>
        </div>
      </div>` : ''}
    </div>
  </div>` : ''}

  <div class="summary">
    <div class="summary-left">
      <div class="summary-title">${es('summaryTitle')}</div>
      <p class="summary-body">${es('summaryBody', { total: fe(total), roi: roiFmt, cost: fe(cataCost) })}</p>
      <div class="summary-metrics">
        <div>
          <div class="summary-metric-num">${mqFmt}</div>
          <div class="summary-metric-lbl">${es('metricQuotes')}</div>
        </div>
        <div>
          <div class="summary-metric-num">${fmtN(ftes)}</div>
          <div class="summary-metric-lbl">${es('metricFtes')}</div>
        </div>
        <div>
          <div class="summary-metric-num">${fe(net)}</div>
          <div class="summary-metric-lbl">${es('metricNet')}</div>
        </div>
      </div>
    </div>
    <div class="summary-right">
      <div class="summary-roi-num">${roiFmt}</div>
      <div class="summary-roi-lbl">${es('roiLbl')}</div>
    </div>
  </div>

  <div class="pilot-strip">
    <div class="pilot-badge">${es('pilotBadge')}</div>
    <div class="pilot-text">
      <div class="pilot-title">${es('pilotTitle')}</div>
      <div class="pilot-body">${es('pilotBody')}</div>
    </div>
  </div>

  <div class="params-strip">
    <div class="params-title">Calculation Parameters</div>
    <div class="params-grid">
      <div class="param-item"><span class="param-lbl">Currency</span><span class="param-val">${currencyLabel} (${sym})</span></div>
      <div class="param-item"><span class="param-lbl">Monthly Quotes</span><span class="param-val">${mqFmt}</span></div>
      <div class="param-item"><span class="param-lbl">FTE Monthly Cost</span><span class="param-val">${fe2(fteCost)}</span></div>
      <div class="param-item"><span class="param-lbl">Max Quotes / FTE</span><span class="param-val">${fteCap}</span></div>
      <div class="param-item"><span class="param-lbl">FTE Productivity Multiplier</span><span class="param-val">${multFmt}</span></div>
      <div class="param-item"><span class="param-lbl">Catax Monthly Cost</span><span class="param-val">${fe(cataCost)}</span></div>
      <div class="param-item"><span class="param-lbl">Avg Order Value</span><span class="param-val">${fe2(avgTotal)}</span></div>
      <div class="param-item"><span class="param-lbl">Avg Parts Value</span><span class="param-val">${fe2(avgParts)}</span></div>
      <div class="param-item"><span class="param-lbl">Error Rate</span><span class="param-val">${parseFloat(errorRate.toFixed(1))}%</span></div>
    </div>
  </div>

</div>
</body>
</html>`;

    const blob = new Blob([html], { type: 'text/html' });
    const url  = URL.createObjectURL(blob);
    const win  = window.open(url, '_blank');
    if (win) {
      win.addEventListener('load', () => URL.revokeObjectURL(url), { once: true });
    }
  }, [roi, multiplier, mq, total, cataCost, net, ftes, fteSaved, labor, bsk, cvp, err, itTotal, itWorkflow, itInteg, errorRate, convUplift, currSym, fteCost, avgTotal, avgParts, fteCap, currentUser, t]);

  return (
    <AppLayout pageTitle={t('roiCalculator.pageTitle')} pageSubtitle={t('roiCalculator.pageSubtitle')}>
      <MobileHeader title={t('roiCalculator.mobileTitle')} showBack={false} onBackClick={() => navigate('/')} />

      {/* Full-height, no outer scroll — inner columns scroll independently if needed */}
      <div className="flex-1 overflow-hidden bg-main-bg flex flex-col">

        {/* ── B2B / B2C mode switcher (only shown when both are enabled) ── */}
        {currentUser?.roi_b2c_enabled && (
        <div className="flex items-center gap-2 px-4 pt-3 pb-0 flex-shrink-0">
          <span className="text-[9px] font-bold text-gray-400 uppercase tracking-widest">Calculator mode</span>
          <div className="flex gap-0.5 bg-gray-100 rounded-lg p-0.5 border border-gray-200">
            <button
              className="text-[11px] font-bold px-3 py-1 rounded-md bg-white text-[#1E0DB8] shadow-sm"
            >
              B2B
            </button>
            <button
              onClick={() => navigate('/roi-calculator/b2c')}              className="text-[11px] font-medium px-3 py-1 rounded-md text-gray-500 hover:text-gray-800 transition-all duration-150"
            >
              B2C
            </button>
          </div>
        </div>
        )}

        {/* ── Two-column layout: inputs+breakdown | results ─────────────── */}
        <div className="flex-1 overflow-hidden grid grid-cols-1 lg:grid-cols-[1fr_320px] xl:grid-cols-[1fr_360px] gap-3 px-4 py-4 min-h-0">

          {/* ══ COL 1 — Inputs + Value Breakdown ══ */}
          <div className="flex flex-col gap-3 overflow-y-auto min-h-0 pr-0.5">

              {/* ── Inputs row ── */}
            <div>
              <p className="text-[9px] font-bold text-gray-400 uppercase tracking-widest mb-2">{t('roiCalculator.sections.inputs')}</p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-2">

                {/* Business volume */}
                <Card title={t('roiCalculator.volume.title')}>
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-[9px] font-bold text-gray-400 uppercase tracking-tight whitespace-nowrap">{t('roiCalculator.volume.modeLabel')}</span>
                    <TabGroup options={[{ id: 'orders', label: t('roiCalculator.volume.ordersMode') }, { id: 'quotes', label: t('roiCalculator.volume.quotesMode') }]} value={volumeMode} onChange={handleVolumeModeChange} />
                  </div>
                  {volumeMode === 'orders' ? (<>
                    <FieldRow label={t('roiCalculator.volume.ordersPerMonth')}><NumInput value={ordersPerMonth} onChange={setOrdersPerMonth} min={100} max={100000} step={100} /></FieldRow>
                    <FieldRow label={t('roiCalculator.volume.quoteToOrderRate')}><NumInput value={convOrders} onChange={setConvOrders} min={1} max={100} unit="%" /></FieldRow>
                    <DerivedRow label={t('roiCalculator.volume.derivedMonthlyQuotes')} value={Math.round(mq).toLocaleString('en-US') + '/mo'} />
                  </>) : (<>
                    <FieldRow label={t('roiCalculator.volume.monthlyQuotes')}><NumInput value={quotesDirectInput} onChange={setQuotesDirectInput} min={100} step={100} w="w-20" /></FieldRow>
                    <FieldRow label={t('roiCalculator.volume.quoteToOrderRate')}><NumInput value={convQuotes} onChange={setConvQuotes} min={1} max={100} unit="%" /></FieldRow>
                    <DerivedRow label={t('roiCalculator.volume.derivedMonthlyQuotes')} value={Math.round(mq).toLocaleString('en-US') + '/mo'} />
                    <DerivedRow label={t('roiCalculator.volume.derivedMonthlyOrders')} value={Math.round(monthlyOrders).toLocaleString('en-US') + '/mo'} />
                  </>)}
                  <Divider />
                  <FieldRow label={t('roiCalculator.volume.avgTotalValue')}><NumInput value={avgTotal} onChange={setAvgTotal} min={10} step={0.01} unit={currSym} w="w-20" /></FieldRow>
                  <FieldRow label={t('roiCalculator.volume.avgPartsValue')}><NumInput value={avgParts} onChange={setAvgParts} min={10} step={0.01} unit={currSym} w="w-20" /></FieldRow>
                </Card>

                {/* Workforce & cost */}
                <Card title={t('roiCalculator.workforce.title')}>
                  <FieldRow label={t('roiCalculator.workforce.maxQuotesPerFte')}><NumInput value={fteCap} onChange={setFteCap} min={50} max={1000} w="w-16" /></FieldRow>
                  <FieldRow label={t('roiCalculator.workforce.fteCost')}><NumInput value={fteCost} onChange={setFteCost} min={500} step={0.01} unit={currSym} w="w-20" /></FieldRow>
                  <Divider />
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-[9px] font-bold text-gray-400 uppercase tracking-tight whitespace-nowrap">{t('roiCalculator.workforce.pricingLabel')}</span>
                    <TabGroup options={[{ id: 'per-quote', label: t('roiCalculator.workforce.perQuoteMode') }, { id: 'fixed', label: t('roiCalculator.workforce.fixedMode') }]} value={costMode} onChange={handleCostModeChange} />
                  </div>
                  {costMode === 'per-quote' ? (<>
                    <FieldRow label={t('roiCalculator.workforce.costPerQuote')}><NumInput value={cataCostPerQ} onChange={setCataCostPerQ} min={0.01} step={0.01} unit={currSym} /></FieldRow>
                    <DerivedRow label={t('roiCalculator.workforce.totalMonthlyCost')} value={fmtE(cataCost)} />
                  </>) : (<>
                    <FieldRow label={t('roiCalculator.workforce.fixedMonthlyFee')}><NumInput value={cataCostFixed} onChange={setCataCostFixed} min={1} step={100} unit={currSym} w="w-20" /></FieldRow>
                    <DerivedRow label={t('roiCalculator.workforce.impliedCostPerQuote')} value={mq > 0 ? fmtE2(cataCostFixed / mq) : '—'} />
                  </>)}
                </Card>

                {/* Performance */}
                <Card title={t('roiCalculator.performance.title')}>
                  <p className="text-[9px] italic text-gray-400 mb-2">{t('roiCalculator.performance.metricsNote')}</p>
                  <FieldRow label={t('roiCalculator.performance.fteMultiplierLabel')} note={t('roiCalculator.performance.fteMultiplierNote')}>
                    <div className="flex items-center gap-0 bg-gray-50 border border-gray-200 rounded-md overflow-hidden flex-shrink-0 focus-within:border-[#1E0DB8] focus-within:bg-white transition-colors">
                      <span className="text-[11px] font-bold text-[#1E0DB8] px-2">×</span>
                      <input
                        type="number" value={fteMultiplier} min={1.1} max={20} step={0.1}
                        onChange={(e) => setFteMultiplier(parseFloat(e.target.value) || 2)}
                        className="w-12 text-[12px] font-bold font-inter-tight text-right py-1 pr-1 text-[#1E0DB8] bg-transparent border-none outline-none [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                      />
                      <span className="text-[10px] text-gray-400 pr-2 whitespace-nowrap">{t('roiCalculator.performance.fteMultiplierSuffix')}</span>
                    </div>
                  </FieldRow>
                  <div className="flex items-center justify-between mb-2 px-0.5">
                    <span className="text-[10px] italic text-gray-400">{t('roiCalculator.performance.timeSavedLabel')}</span>
                    <span className="text-[10px] font-bold text-[#3627BF]">{t('roiCalculator.performance.timeSavedValue', { pct: Math.round(timeSaved * 100) })}</span>
                  </div>
                  <FieldRow label={t('roiCalculator.performance.basketUplift')} note={t('roiCalculator.performance.basketUpliftNote')}><NumInput value={basketUplift} onChange={setBasketUplift} min={0} max={50} step={0.1} unit="%" /></FieldRow>
                  <FieldRow label={t('roiCalculator.performance.convUplift')} note={t('roiCalculator.performance.convUpliftNote')}><NumInput value={convUplift} onChange={setConvUplift} min={0} max={50} step={0.1} unit="%" /></FieldRow>
                  <FieldRow label={t('roiCalculator.performance.errorRate')}><NumInput value={errorRate} onChange={setErrorRate} min={0} max={50} step={0.1} unit="%" /></FieldRow>
                  <FieldRow label={t('roiCalculator.performance.logisticsPenalty')} note={t('roiCalculator.performance.logisticsPenaltyNote')}><NumInput value={revLogistics} onChange={setRevLogistics} min={0} max={100} step={0.1} unit="%" /></FieldRow>
                </Card>

              </div>
            </div>            {/* ── Value Breakdown row ── */}
            <div>
              <p className="text-[9px] font-bold text-gray-400 uppercase tracking-widest mb-2">{t('roiCalculator.sections.valueBreakdown')}</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">

                {/* Operational breakdown */}
                <Card title={t('roiCalculator.breakdown.operationalTitle')}>
                  {/* Workforce */}
                  <div className="flex items-start justify-between gap-2 py-2 border-b border-gray-100">
                    <div className="flex items-start gap-1.5 flex-1 min-w-0">
                      <div className="w-1.5 h-1.5 rounded-sm bg-[#1E0DB8] flex-shrink-0 mt-1.5" />
                      <div className="min-w-0">
                        <p className="text-xs font-bold text-gray-900">{t('roiCalculator.breakdown.workforce')}</p>
                        <p className="text-[9px] italic text-gray-400">{t('roiCalculator.breakdown.workforceFormula')}</p>
                        <div className="inline-flex items-center gap-1 bg-[#E5E2FF] rounded px-1.5 py-0.5 mt-1">
                          <Users className="w-2.5 h-2.5 text-[#3627BF]" />
                          <span className="text-[9px] font-bold text-[#3627BF]">{t('roiCalculator.breakdown.workforceBadge', { saved: fmtN(fteSaved) })}</span>
                          <span className="text-[9px] text-[#8880d0]">{t('roiCalculator.breakdown.workforceBadgeDetail', { mult: parseFloat(multiplier.toFixed(1)) })}</span>
                        </div>
                      </div>
                    </div>
                    <span className="font-inter-tight text-xs font-semibold text-[#1E0DB8] flex-shrink-0">{fmtE(labor)}</span>
                  </div>
                  {/* 24/7 quoting */}
                  <div className="flex items-start justify-between gap-2 py-2 border-b border-gray-100">
                    <div className="flex items-start gap-1.5 flex-1 min-w-0">
                      <div className="w-1.5 h-1.5 rounded-sm bg-[#137F67] flex-shrink-0 mt-1.5" />
                      <div>
                        <p className="text-xs font-bold text-gray-900">{t('roiCalculator.breakdown.quoting')}</p>
                        <p className="text-[9px] italic text-gray-400">{t('roiCalculator.breakdown.quotingFormula')}</p>
                      </div>
                    </div>
                    <span className="font-inter-tight text-xs font-semibold text-[#1E0DB8] flex-shrink-0">{fmtE(bsk)}</span>
                  </div>
                  {/* Upselling */}
                  <div className="flex items-start justify-between gap-2 py-2 border-b border-gray-100">
                    <div className="flex items-start gap-1.5 flex-1 min-w-0">
                      <div className="w-1.5 h-1.5 rounded-sm bg-[#3627BF] flex-shrink-0 mt-1.5" />
                      <div>
                        <p className="text-xs font-bold text-gray-900">{t('roiCalculator.breakdown.upsell')}</p>
                        <p className="text-[9px] italic text-gray-400">{t('roiCalculator.breakdown.upsellFormula')}</p>
                      </div>
                    </div>
                    <span className="font-inter-tight text-xs font-semibold text-[#1E0DB8] flex-shrink-0">{fmtE(cvp)}</span>
                  </div>
                  {/* Error elimination */}
                  <div className="flex items-start justify-between gap-2 py-2">
                    <div className="flex items-start gap-1.5 flex-1 min-w-0">
                      <div className="w-1.5 h-1.5 rounded-sm bg-[#DBD8FF] border border-gray-300 flex-shrink-0 mt-1.5" />
                      <div>
                        <p className="text-xs font-bold text-gray-900">{t('roiCalculator.breakdown.errors')}</p>
                        <p className="text-[9px] italic text-gray-400">{t('roiCalculator.breakdown.errorsFormula')}</p>
                      </div>
                    </div>
                    <span className="font-inter-tight text-xs font-semibold text-[#1E0DB8] flex-shrink-0">{fmtE(err)}</span>
                  </div>
                  {/* Op total */}
                  <div className="flex items-center justify-between pt-2 border-t border-gray-200">
                    <span className="text-xs font-bold text-gray-900">{t('roiCalculator.breakdown.opTotal')}</span>
                    <span className="font-inter-tight text-base font-semibold text-[#1E0DB8]">{fmtE(opTotal)}</span>
                  </div>
                </Card>

                {/* IT value */}
                <Card title={t('roiCalculator.breakdown.itTitle')}>
                  <p className="text-[9px] italic text-gray-400 mb-2">{t('roiCalculator.breakdown.itNote')}</p>
                  <div className="flex items-center justify-between gap-2 py-1.5 border-b border-gray-100">
                    <div className="flex-1 min-w-0">
                      <p className="text-[11px] font-bold text-gray-900">{t('roiCalculator.breakdown.itWorkflow')}</p>
                      <p className="text-[9px] italic text-gray-400">{t('roiCalculator.breakdown.itWorkflowNote')}</p>
                    </div>
                    <input type="number" value={itWorkflow === '' ? '' : itWorkflow} placeholder={currSym} min={0} step={1000}
                      onChange={(e) => setItWorkflow(e.target.value === '' ? '' : parseFloat(e.target.value) || 0)}
                      className="w-20 text-[11px] font-medium text-right px-1.5 py-1 rounded-md border border-gray-200 bg-[#E5E2FF] text-[#1E0DB8] outline-none focus:border-[#1E0DB8] focus:bg-white transition-colors placeholder:text-[#b0aee0] placeholder:italic [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none flex-shrink-0"
                    />
                  </div>
                  <div className="flex items-center justify-between gap-2 py-1.5">
                    <div className="flex-1 min-w-0">
                      <p className="text-[11px] font-bold text-gray-900">{t('roiCalculator.breakdown.itInteg')}</p>
                      <p className="text-[9px] italic text-gray-400">{t('roiCalculator.breakdown.itIntegNote')}</p>
                    </div>
                    <input type="number" value={itInteg === '' ? '' : itInteg} placeholder={currSym} min={0} step={1000}
                      onChange={(e) => setItInteg(e.target.value === '' ? '' : parseFloat(e.target.value) || 0)}
                      className="w-20 text-[11px] font-medium text-right px-1.5 py-1 rounded-md border border-gray-200 bg-[#E5E2FF] text-[#1E0DB8] outline-none focus:border-[#1E0DB8] focus:bg-white transition-colors placeholder:text-[#b0aee0] placeholder:italic [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none flex-shrink-0"
                    />
                  </div>
                  <div className="flex items-center justify-between pt-2 border-t border-gray-200">
                    <span className="text-[11px] font-bold text-gray-900">{t('roiCalculator.breakdown.itTotal')}</span>
                    <span className={`font-inter-tight text-sm font-semibold ${itTotal > 0 ? 'text-[#1E0DB8]' : 'text-gray-400'}`}>
                      {itTotal > 0 ? fmtE(itTotal) : '—'}
                    </span>
                  </div>
                </Card>

              </div>
            </div>
          </div>

          {/* ══ COL 2 — Results (sticky right panel) ══ */}
          <div className="flex flex-col gap-2 overflow-y-auto min-h-0 pr-0.5">
            <p className="text-[9px] font-bold text-gray-400 uppercase tracking-widest flex-shrink-0">{t('roiCalculator.sections.results')}</p>

            {/* KPI chips */}
            <div className="grid grid-cols-2 gap-2 flex-shrink-0">
              <Card>
                <p className="text-[9px] font-bold text-gray-400 uppercase tracking-widest mb-1">{t('roiCalculator.results.monthlyQuotes')}</p>
                <p className="font-inter-tight text-xl font-medium text-gray-900 leading-none">{Math.round(mq).toLocaleString('en-US')}</p>
              </Card>
              <Card>
                <p className="text-[9px] font-bold text-gray-400 uppercase tracking-widest mb-1">{t('roiCalculator.results.requiredFtes')}</p>
                <p className="font-inter-tight text-xl font-medium text-gray-900 leading-none">{fmtN(ftes)}</p>
              </Card>
            </div>

            {/* Cost summary */}
            <div className="grid grid-cols-2 gap-2 flex-shrink-0">
              <Card>
                <p className="text-[9px] font-bold text-gray-400 uppercase tracking-widest mb-1">{t('roiCalculator.results.cataCostPerMonth')}</p>
                <p className="font-inter-tight text-lg font-medium text-gray-900 leading-none">{fmtE(cataCost)}</p>
                <p className="text-[9px] text-gray-400 mt-1">
                  {costMode === 'fixed'
                    ? t('roiCalculator.results.costFixed', { cpq: fmtE2(cpqDisplay) })
                    : t('roiCalculator.results.costPerQuote', { cpq: fmtE2(cpqDisplay) })}
                </p>
              </Card>
              <Card>
                <p className="text-[9px] font-bold text-gray-400 uppercase tracking-widest mb-1">{t('roiCalculator.results.netValuePerMonth')}</p>
                <p className="font-inter-tight text-lg font-medium text-[#137F67] leading-none">{fmtE(net)}</p>
              </Card>
            </div>

            {/* ROI card */}
            <div className="flex-shrink-0 bg-[#1E0DB8] rounded-xl px-4 py-3 flex items-center justify-between">
              <div>
                <p className="text-[9px] font-bold text-[#DBD8FF] uppercase tracking-wider mb-0.5">{t('roiCalculator.results.roiLabel')}</p>
                <p className="text-[9px] text-[#DBD8FF]/60">{t('roiCalculator.results.roiFormula')}</p>
                <div className="mt-2 flex items-baseline gap-2">
                  <span className="font-inter-tight text-xs text-[#DBD8FF]">{t('roiCalculator.results.totalValue')}</span>
                  <span className="font-inter-tight text-sm font-medium text-white">{fmtE(total)}</span>
                </div>
              </div>
              <p className="font-inter-tight text-5xl font-medium text-white leading-none">
                {Math.round(roi).toLocaleString('en-US')}%
              </p>
            </div>

            {/* Chart */}
            <Card title={t('roiCalculator.results.chartTitle')} className="flex-shrink-0">
              <div style={{ height: 140 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={chartData} margin={{ top: 4, right: 4, left: -16, bottom: 0 }} barCategoryGap="35%">
                    <XAxis dataKey="name" tick={{ fontSize: 9, fontFamily: 'Plus Jakarta Sans', fill: '#999', fontWeight: 500 }} axisLine={false} tickLine={false} />
                    <YAxis tick={{ fontSize: 9, fontFamily: 'Plus Jakarta Sans', fill: '#999' }} axisLine={false} tickLine={false}
                      tickFormatter={(v) => currSym + (v >= 1000 ? Math.round(v / 1000) + 'k' : v)} />
                    <Tooltip content={<CustomTooltip sym={currSym} />} cursor={{ fill: 'rgba(32,30,26,0.04)' }} />
                    <Bar dataKey="value" radius={[4, 4, 0, 0]}>
                      {chartData.map((entry, i) => <Cell key={i} fill={entry.color} />)}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </Card>

            {/* Executive Summary button */}
            <button
              onClick={generateExecutiveSummary}
              className="flex-shrink-0 flex items-center justify-center gap-2 w-full bg-[#1E0DB8] hover:bg-[#3627BF] active:bg-[#1a0aa0] text-white text-[12px] font-bold rounded-xl px-4 py-2.5 transition-colors duration-150 shadow-sm"
            >
              <FileText className="w-3.5 h-3.5" />
              {t('roiCalculator.results.executiveSummaryBtn')}
            </button>

            {/* Pilot banner */}
            <div className="flex-shrink-0 bg-[#E5E2FF] rounded-xl px-3 py-2.5">
              <div className="flex items-center gap-2 mb-1">
                <span className="bg-[#1E0DB8] text-white text-[8px] font-bold uppercase tracking-wider rounded px-1.5 py-0.5">{t('roiCalculator.results.pilotBadge')}</span>
                <span className="text-[11px] font-bold text-[#1E0DB8]">{t('roiCalculator.results.pilotTitle')}</span>
              </div>
              <p className="text-[10px] font-medium text-[#3627BF] leading-relaxed">
                {t('roiCalculator.results.pilotBody')}
              </p>
            </div>
          </div>

        </div>
      </div>
    </AppLayout>
  );
};

export default ROICalculatorPage;
